import { useMemo } from 'react';
// Translation
import { useTranslationContext } from '../../context/TranslationContext';
// Components/ui
import { Typography, Fade, Box } from '@mui/material';
import { StyledDatePickerContainer, StyledPickersContainer, StyledPickersDivider } from './styles';
import {
  DatePicker as MuiDatePicker,
  TimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

const DATE_FORMAT = 'DD/MM/YYYY';
const TIME_FORMAT = 'HH:mm';

type DatePickerProps = {
  selectedStartDate: Dayjs;
  selectedEndDate: Dayjs;
  handleSelectStart: (date: Dayjs) => void;
  handleSelectEnd: (date: Dayjs) => void;
  isError: boolean;
  disabled: boolean;
  hideLabel?: boolean; // Nueva prop para ocultar el label
  allDay?: boolean; // Prop para modo todo el día
};

const DatePicker = ({
  selectedStartDate,
  selectedEndDate,
  handleSelectStart,
  handleSelectEnd,
  isError,
  disabled,
  hideLabel = false,
  allDay = false,
}: DatePickerProps) => {
  const t = useTranslationContext();

  // Calcular la duración en minutos
  const durationMinutes = useMemo(() => {
    return selectedEndDate.diff(selectedStartDate, 'minute');
  }, [selectedStartDate, selectedEndDate]);

  // Formatear duración para mostrar (ej: "1 h", "30 min", "1.5 h")
  const durationLabel = useMemo(() => {
    if (durationMinutes === 0) return '0 min';
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} h`;
    return `${hours}.${Math.round((minutes / 60) * 10)} h`;
  }, [durationMinutes]);

  // Manejar cambio de fecha (aplica a inicio y fin, manteniendo las horas)
  const handleDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;

    // Mantener las horas actuales, solo cambiar la fecha
    const newStartDate = newDate
      .hour(selectedStartDate.hour())
      .minute(selectedStartDate.minute())
      .second(0)
      .millisecond(0);

    const newEndDate = newDate
      .hour(selectedEndDate.hour())
      .minute(selectedEndDate.minute())
      .second(0)
      .millisecond(0);

    handleSelectStart(newStartDate);
    handleSelectEnd(newEndDate);
  };

  // Manejar cambio de fecha de inicio (para modo allDay)
  const handleStartDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;

    const newStartDate = newDate.hour(0).minute(0).second(0).millisecond(0);

    handleSelectStart(newStartDate);
  };

  // Manejar cambio de fecha de fin (para modo allDay)
  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (!newDate) return;

    const newEndDate = newDate.hour(23).minute(59).second(59).millisecond(999);

    handleSelectEnd(newEndDate);
  };

  // Manejar cambio de hora de inicio
  const handleStartTimeChange = (newTime: Dayjs | null) => {
    if (!newTime) return;

    // Mantener la fecha actual, solo cambiar la hora
    const newStartDate = selectedStartDate
      .hour(newTime.hour())
      .minute(newTime.minute())
      .second(0)
      .millisecond(0);

    // Calcular la nueva hora de fin manteniendo el intervalo original
    const newEndDate = newStartDate.add(durationMinutes, 'minute');

    handleSelectStart(newStartDate);
    handleSelectEnd(newEndDate);
  };

  // Manejar cambio de hora de fin
  const handleEndTimeChange = (newTime: Dayjs | null) => {
    if (!newTime) return;

    // Mantener la fecha actual, solo cambiar la hora
    // Si la hora de fin es menor que la de inicio, asumimos que es al día siguiente
    let newEndDate = selectedStartDate
      .hour(newTime.hour())
      .minute(newTime.minute())
      .second(0)
      .millisecond(0);

    // Si la hora de fin es menor o igual a la de inicio, sumar un día
    if (newEndDate.isBefore(selectedStartDate) || newEndDate.isSame(selectedStartDate)) {
      newEndDate = newEndDate.add(1, 'day');
    }

    handleSelectEnd(newEndDate);
  };

  // Modo vista: mostrar las fechas como texto estático
  if (disabled) {
    return (
      <StyledDatePickerContainer>
        {!hideLabel && (
          <span
            style={{
              userSelect: 'none',
              marginBottom: 10,
            }}
          >
            {t('global.date')}
          </span>
        )}

        {allDay ? (
          // Vista de todo el día: mostrar rango de fechas
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '10px' }}>
            <Typography variant="body1">{selectedStartDate.format(DATE_FORMAT)}</Typography>
            <Typography variant="body1" color="text.secondary">
              –
            </Typography>
            <Typography variant="body1">{selectedEndDate.format(DATE_FORMAT)}</Typography>
          </Box>
        ) : (
          // Vista normal: fecha + horas
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {selectedStartDate.format(DATE_FORMAT)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">{selectedStartDate.format(TIME_FORMAT)}</Typography>
              <Typography variant="body1" color="text.secondary">
                –
              </Typography>
              <Typography variant="body1">{selectedEndDate.format(TIME_FORMAT)}</Typography>
              {durationMinutes > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    ml: 1,
                  }}
                >
                  ({durationLabel})
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </StyledDatePickerContainer>
    );
  }

  return (
    <StyledDatePickerContainer>
      {!hideLabel && (
        <span
          style={{
            userSelect: 'none',
            marginBottom: 10,
          }}
        >
          {t('global.date')}
        </span>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {allDay ? (
          // Modo Todo el día: 2 selectores de fecha
          <StyledPickersContainer>
            <MuiDatePicker
              disabled={disabled}
              format={DATE_FORMAT}
              label={t('global.from')}
              slotProps={{
                textField: () => ({
                  size: 'small',
                }),
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      //@ts-ignore
                      backgroundColor: (theme) => theme.palette.background.panel,
                    },
                  },
                },
              }}
              value={selectedStartDate}
              onChange={handleStartDateChange}
              minDate={dayjs()}
              sx={{ flex: 1 }}
            />

            <StyledPickersDivider variant="body1">–</StyledPickersDivider>

            <MuiDatePicker
              disabled={disabled}
              format={DATE_FORMAT}
              label={t('global.to')}
              slotProps={{
                textField: () => ({
                  size: 'small',
                }),
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      //@ts-ignore
                      backgroundColor: (theme) => theme.palette.background.panel,
                    },
                  },
                },
              }}
              value={selectedEndDate}
              onChange={handleEndDateChange}
              minDate={selectedStartDate}
              sx={{ flex: 1 }}
            />
          </StyledPickersContainer>
        ) : (
          // Modo normal: 1 fecha + 2 horas
          <>
            {/* Selector de fecha único */}
            <Box sx={{ mb: 1 }}>
              <MuiDatePicker
                disabled={disabled}
                format={DATE_FORMAT}
                slotProps={{
                  textField: () => ({
                    size: 'small',
                    fullWidth: true,
                  }),
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        //@ts-ignore
                        backgroundColor: (theme) => theme.palette.background.panel,
                      },
                    },
                  },
                }}
                value={selectedStartDate}
                onChange={handleDateChange}
                minDate={dayjs()}
              />
            </Box>

            {/* Selectores de hora */}
            <StyledPickersContainer>
              <TimePicker
                disabled={disabled}
                format={TIME_FORMAT}
                ampm={false}
                slotProps={{
                  textField: () => ({
                    label: t('global.from'),
                    size: 'small',
                    className: 'smallInput',
                  }),
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        //@ts-ignore
                        backgroundColor: (theme) => theme.palette.background.panel,
                      },
                    },
                  },
                }}
                value={selectedStartDate}
                onChange={handleStartTimeChange}
                sx={{ alignSelf: 'baseline', flex: 1 }}
              />

              <StyledPickersDivider variant="body1">–</StyledPickersDivider>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <TimePicker
                  disabled={disabled}
                  format={TIME_FORMAT}
                  ampm={false}
                  slotProps={{
                    textField: () => ({
                      label: t('global.to'),
                      size: 'small',
                      className: 'smallInput',
                    }),
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          //@ts-ignore
                          backgroundColor: (theme) => theme.palette.background.panel,
                        },
                      },
                    },
                  }}
                  value={selectedEndDate}
                  onChange={handleEndTimeChange}
                  sx={{ alignSelf: 'baseline', width: '100%' }}
                />
                {durationMinutes > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      ml: 1.5,
                    }}
                  >
                    ({durationLabel})
                  </Typography>
                )}
              </Box>
            </StyledPickersContainer>
          </>
        )}
      </LocalizationProvider>
      {isError && (
        <Fade in={isError}>
          <Typography color={'red'} variant="caption" sx={{ mt: 1, ml: '10px' }}>
            {t('calendarView.reminders.errorDate')}
          </Typography>
        </Fade>
      )}
    </StyledDatePickerContainer>
  );
};

export default DatePicker;
